import React, { useEffect, useRef, useState } from 'react';
import { Button, Card } from '@/components/UI';
import type { PortfolioBacktestColumnConfig, PortfolioBacktestColumnId } from './portfolioBacktestColumnConfig';

interface PortfolioBacktestColumnVisibilityControlProps {
    availableColumns: PortfolioBacktestColumnConfig[];
    visibleColumns: Set<PortfolioBacktestColumnId>;
    /** Visible columns holding one value across every filtered row, so they separate nothing right now. */
    uniformColumnIds: Set<PortfolioBacktestColumnId>;
    onHideUniformColumns: () => void;
    onPresetSelect: (preset: 'compact' | 'performance' | 'full') => void;
    onReset: () => void;
    onVisibilityChange: (columns: Set<PortfolioBacktestColumnId>) => void;
}

const PortfolioBacktestColumnVisibilityControl: React.FC<PortfolioBacktestColumnVisibilityControlProps> = ({
    availableColumns,
    visibleColumns,
    uniformColumnIds,
    onHideUniformColumns,
    onPresetSelect,
    onReset,
    onVisibilityChange
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const buttonRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                buttonRef.current &&
                !buttonRef.current.contains(event.target as Node) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const handleColumnToggle = (columnId: PortfolioBacktestColumnId) => {
        const nextColumns = new Set(visibleColumns);

        if (nextColumns.has(columnId)) {
            if (visibleColumns.size === 1) {
                return;
            }

            nextColumns.delete(columnId);
        } else {
            nextColumns.add(columnId);
        }

        onVisibilityChange(nextColumns);
    };

    return (
        <div className="relative">
            <div ref={buttonRef}>
                <Button
                    aria-expanded={isOpen}
                    aria-haspopup="dialog"
                    className="flex items-center gap-2"
                    variant="secondary"
                    onClick={() => setIsOpen((current) => !current)}
                >
                    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                        />
                        <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                    </svg>
                    Columns ({visibleColumns.size})
                </Button>
            </div>

            {isOpen && (
                <div className="absolute right-0 z-50 mt-2" ref={dropdownRef}>
                    <Card responsivePadding className="w-80">
                        <div className="space-y-4">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="text-base font-semibold text-white">Visible Columns</div>
                                    <div className="text-sm text-zinc-400">Saved in this browser for future sessions.</div>
                                </div>
                                <button
                                    aria-label="Close column settings"
                                    className="text-zinc-400 transition-colors hover:text-zinc-200"
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                >
                                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} />
                                    </svg>
                                </button>
                            </div>

                            <div className="flex flex-wrap gap-2">
                                <Button size="sm" variant="secondary" onClick={() => onPresetSelect('compact')}>
                                    Compact
                                </Button>
                                <Button size="sm" variant="secondary" onClick={() => onPresetSelect('performance')}>
                                    Performance
                                </Button>
                                <Button size="sm" variant="secondary" onClick={() => onPresetSelect('full')}>
                                    Full
                                </Button>
                                <Button size="sm" variant="outline-dark" onClick={onReset}>
                                    Reset
                                </Button>
                            </div>

                            {uniformColumnIds.size > 0 && uniformColumnIds.size < visibleColumns.size && (
                                <div className="rounded border border-zinc-700 bg-zinc-950/40 p-3">
                                    <div className="text-sm text-zinc-300">
                                        {uniformColumnIds.size.toLocaleString()} visible{' '}
                                        {uniformColumnIds.size === 1 ? 'column holds' : 'columns hold'} the same value on every matching
                                        backtest.
                                    </div>
                                    <Button className="mt-2" size="sm" variant="secondary" onClick={onHideUniformColumns}>
                                        Hide {uniformColumnIds.size === 1 ? 'it' : 'them'}
                                    </Button>
                                </div>
                            )}

                            <div className="max-h-80 space-y-2 overflow-y-auto pr-1">
                                {availableColumns.map((column) => {
                                    const isVisible = visibleColumns.has(column.id);
                                    const isLastVisibleColumn = isVisible && visibleColumns.size === 1;

                                    return (
                                        <label
                                            className={`flex items-center gap-2 rounded px-2 py-1 text-sm transition-colors ${
                                                isLastVisibleColumn
                                                    ? 'cursor-not-allowed text-zinc-500'
                                                    : 'cursor-pointer text-zinc-300 hover:bg-zinc-800'
                                            }`}
                                            key={column.id}
                                        >
                                            <input
                                                checked={isVisible}
                                                className="h-4 w-4 rounded border-zinc-600 bg-zinc-700 text-blue-600 focus:ring-2 focus:ring-blue-500"
                                                disabled={isLastVisibleColumn}
                                                type="checkbox"
                                                onChange={() => handleColumnToggle(column.id)}
                                            />
                                            <span>{column.label}</span>
                                            {uniformColumnIds.has(column.id) && (
                                                <span className="text-xs text-zinc-500" title="Same value on every matching backtest">
                                                    uniform
                                                </span>
                                            )}
                                        </label>
                                    );
                                })}
                            </div>

                            <div className="text-xs text-zinc-500">At least one backtest data column stays visible.</div>
                        </div>
                    </Card>
                </div>
            )}
        </div>
    );
};

export default PortfolioBacktestColumnVisibilityControl;
